import {
  Card,
  CardBody,
  CardHeader,
  Flex,
  HStack,
  Link,
  Text,
} from "@chakra-ui/react";
import { Link as RouterLink, generatePath } from "react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { API_ROUTES, SHARED_ROUTES } from "definitions/constants/routeConstants";
import apiClient from "services/ApiClient";
import { ResponseModel } from "services/ApiClient/responseModel";
import { User } from "features/User/models";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useCaminoStore from "hooks/useCaminoStore";
import AppPage from "layouts/AppPage";
import NewAdminEmployeeButtonWithModal from "components/ModalForm/NewAdminEmployeeButtonWithModal";
import GenericTable, { GenericTableProps, simpleColumn } from "components/Table/GenericTable";
import useNavbar from "hooks/useNavbar";
import { getAvailableRolesOptions } from "features/Role/helpers";
import { adminKeys } from "features/Admin/hooks";
import SearchBar from "components/SearchBar";
import { compact, debounce } from "lodash";
import { Company } from "models/company";
import AdminMenuButton from "features/Admin/AdminMenuButton";
import GreenRedTag from "components/Tag/GreenRedTag";
import SimpleTag from "components/Tag/SimpleTag";
import { titleize } from "helpers/string";
import CompanySelect from "./CompanySelect";

const columnHelper = createColumnHelper<User>();

const makeColumns = () => [
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    cell: (row) => (
      <Link
        as={RouterLink}
        to={{
          pathname: generatePath(SHARED_ROUTES.showUser, { id: row.getValue().id }),
        }}
      >
        {row.getValue().fullName}
      </Link>
    ),
    enableMultiSort: true,
  }),
  columnHelper.accessor((row) => row, {
    id: "status",
    header: "",
    cell: (row) => (
      <Flex gap="1">
        <GreenRedTag isTrue={!row.getValue()?.deactivatedAt} isTrueText="Active" isFalseText="Deactivated" />
        {
          !row.getValue()?.deactivatedAt
            ? (row.getValue().adminPermissions || []).map((permission) => (
              <SimpleTag key={`${row.getValue().id}-${permission}`}>
                {titleize(permission)}
              </SimpleTag>
            ))
            : null
        }
      </Flex>
    ),
    enableSorting: false,
  }),
  simpleColumn({ attribute: "workEmail", label: "Work Email" }, columnHelper, true),
  columnHelper.accessor((row) => row, {
    id: "company",
    header: "Company",
    cell: (row) => row.getValue().company?.name,
    enableMultiSort: true,
  }),
  columnHelper.accessor((row) => row, {
    id: "permission",
    header: "Roles",
    enableSorting: false,
    cell: (row) => (
      <Text fontSize="xs">
        {getAvailableRolesOptions(row.getValue().permission)
          .map((value) => value.label)
          .join(", ")}
      </Text>
    ),
  }),
  columnHelper.accessor((row) => row, {
    id: "menu",
    header: "",
    enableSorting: false,
    cell: (row) => <AdminMenuButton user={row.getValue()} />,
  }),
];

const baseQueryKey: string[] = [...adminKeys.users()];

export default function UsersPage() {
  const [queryKey, setQueryKey] = useState([...baseQueryKey]);
  const isImpersonating = useCaminoStore((state) => state.isImpersonating);
  const queryClient = useQueryClient();
  const { setPageTitle } = useNavbar();
  const [companyFilter, setCompanyFilter] = useState(undefined as Pick<Company, "id" | "name"> | undefined);
  const [query, setQuery] = useState("");
  const debouncedSetQuery = useMemo(
    () => debounce((value: string) => setQuery(value), 300),
    [],
  );

  useEffect(() => {
    setQueryKey(compact([...baseQueryKey, companyFilter?.id, query]));
  }, [setQueryKey, baseQueryKey, companyFilter, query]);

  useEffect(() => {
    setPageTitle("Users");
  }, [setPageTitle]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, isImpersonating]);

  useEffect(() => () => {
    debouncedSetQuery.cancel();
  }, [debouncedSetQuery]);

  const tableProps: GenericTableProps<User> = {
    fetchData: (query, signal) => apiClient.performRequest<ResponseModel<User[]>>(`${API_ROUTES.admin.users.base}${query}`, undefined, signal)
      .then((response) => response.payload),
    queryKey,
    columns: makeColumns(),
    meta: {
      pagination: { perPage: 100 },
      filters: companyFilter ? [{ fieldName: "companyId", value: companyFilter.id, operator: "=" }] : undefined,
    },
    additionalQueries: query ? { q: query } : undefined,
  };

  return (
    <AppPage>
      <Card mt="8" p="4">
        <CardHeader>
          <HStack justify="space-between">
            <SearchBar onInput={debouncedSetQuery} />
            <CompanySelect value={companyFilter} onChange={setCompanyFilter} />
            <NewAdminEmployeeButtonWithModal queryKey={queryKey} />
          </HStack>
        </CardHeader>
        <CardBody><GenericTable {...tableProps} /></CardBody>
      </Card>
    </AppPage>
  );
}
